import ExcelJS, { Workbook } from 'exceljs';
import { saveAs } from 'file-saver';
import { StyledButtonGrid } from '../../ScenarioCreation.Styled';
import CustomButton from '../../../../components/UI/CustomButton/CustomButton';
import { useAppDispatch, useAppSelector } from '../../../../state/hooks';
import { downloadBasePriceFile, uploadBasePriceFile } from '../../../../features/scenarioLibrary/scenarioLibrarySlice';
import { styled } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadForOfflineRoundedIcon from '@mui/icons-material/DownloadForOfflineRounded';
import React, { useRef } from 'react';
import { RootState } from '../../../../state/store';
import Swal from 'sweetalert2';

type ValidationRule = {
  column: string;
  type: ExcelJS.DataValidation['type'];
  formulae: (cellAddress: string) => any[];
  prompt?: string;
  errorTitle?: string;
  error?: string;
};

type UpdateBasePriceProps = {
  modelState: () => void;
};

const downloadWithValidations = async (
  data: any[],
  rulesConfig: {
    validation_rules: ValidationRule[];
    editableKeys: string[];
  },
  workbook: Workbook,
  sheetName = 'Editable Price Sheet',
): Promise<Workbook> => {
  const worksheet = workbook.addWorksheet(sheetName);
  const { validation_rules, editableKeys } = rulesConfig;

  const columns = Object.keys(data[0]).map((key) => ({
    header: key,
    key: key,
    width: 20,
  }));

  worksheet.columns = columns;

  data.forEach((row) => worksheet.addRow(row));

  if (editableKeys.length > 0) {
    validation_rules.forEach(({ column, type, formulae, prompt, errorTitle, error }) => {
      worksheet.getColumn(column).eachCell((cell, rowNumber) => {
        if (rowNumber > 1) {
          const cellAddress = cell.address;
          cell.dataValidation = {
            type,
            allowBlank: false,
            formulae: formulae(cellAddress),
            showInputMessage: true,
            prompt: prompt || 'Invalid input',
            errorTitle: errorTitle || 'Validation Error',
            error: error || 'Invalid value entered.',
            showErrorMessage: true,
          };
        }
      });
    });
  }

  worksheet.columns.forEach((column: any) => {
    if (!column.key || editableKeys.includes(column.key)) return;

    column.eachCell({ includeEmpty: false }, (cell: any, rowNumber: number) => {
      if (rowNumber <= 1) return;

      const originalValue = cell.value;
      cell.dataValidation = {
        type: 'list',
        formulae: [`"${originalValue}"`],
        allowBlank: false,
        showInputMessage: true,
        prompt: 'This value cannot be changed.',
        errorTitle: 'Restricted',
        error: 'Editing this cell is not allowed!',
        showErrorMessage: true,
      };
    });
  });

  // Prevent new rows
  const lastRow = worksheet.rowCount + 1;
  const lastColumn = worksheet.columnCount;
  for (let col = 1; col <= lastColumn; col++) {
    const cell = worksheet.getCell(lastRow, col);
    cell.dataValidation = {
      type: 'custom',
      formulae: ['FALSE'],
      allowBlank: false,
      showInputMessage: true,
      prompt: 'New rows cannot be added.',
      errorTitle: 'Invalid Row',
      error: 'You cannot add new rows in this sheet.',
      showErrorMessage: true,
    };
  }

  return workbook;
};


const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const DownloadEditableExcel: React.FC<UpdateBasePriceProps> = ({ modelState }) => {
  const { isDownloadFileLoading: isLoading, loading: isUploadFileLoading } = useAppSelector(
    (state: RootState) => state.scenarioLibrary,
  );
  const dispatch = useAppDispatch();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleDownload = async () => {
    const downloadFileRes = await dispatch(downloadBasePriceFile({})).unwrap();

    const workbook = new ExcelJS.Workbook();

    const rulesConfig: any = {
      editableKeys: ['new_price'],
      validation_rules: [
        {
          column: 'new_price',
          type: 'decimal',
          formulae: () => ['0'],
          prompt: 'Enter a numeric value',
          errorTitle: 'Invalid Input',
          error: 'Only numbers are allowed in this column.',
        },
      ],
    };

    const finalWorkbook = await downloadWithValidations(downloadFileRes, rulesConfig, workbook);
    const buffer = await finalWorkbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, 'BasePriceUpdate.xlsx');
  };

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    const payloadFormData = new FormData();

    if (file) {
      payloadFormData.append('file', file);

      await dispatch(uploadBasePriceFile(payloadFormData))
        .unwrap()
        .then((res) => {
          if (res?.message) {
            Swal.fire({
              title: 'Success!',
              text: res.message,
              icon: 'success',
              confirmButtonText: 'OK',
              confirmButtonColor: '#E21824',
            })
            .then(() => {
              modelState();
            })
          }
        })
        .catch((err) => {
          console.log('ERR', err);
          Swal.fire({
              title: 'Error!',
              text: `Something went wrong!`,
              icon: 'error',
              confirmButtonText: 'OK',
              confirmButtonColor: '#E21824',
            })
            .then(() => {
              modelState();
            })
        });

      if (inputRef.current) {
        inputRef.current.value = '';
      }
    }
  };

  return (
      <StyledButtonGrid height={'inherit'} justifyContent={'space-between'} gap={30}>
        <CustomButton
          onClick={handleDownload}
          style={{ marginTop: '5px' }}
          isLoading={isLoading}
          startIcon={<DownloadForOfflineRoundedIcon />}
        >
          Download
        </CustomButton>

        <CustomButton
          component="label"
          role={undefined}
          variant="contained"
          tabIndex={-1}
          startIcon={<CloudUploadIcon />}
          onClick={() => {}}
          style={{ marginTop: '5px' }}
          isLoading={isUploadFileLoading}
        >
          Upload file
          <VisuallyHiddenInput type="file" ref={inputRef} onChange={handleUpload} accept=".xlsx" />
        </CustomButton>
      </StyledButtonGrid>
  );
};

export default DownloadEditableExcel;
